import React, { useState, useEffect, useRef, useCallback, useMemo, useReducer } from 'react';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { ArrowLeft, ArrowRight, Star, Repeat, Loader2 } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AssistantResource from 'Resources/AssistantChat';
import AssistantRoutinesResource from 'Resources/AssistantRoutines';
import AgentRunsResource, { type AgentRun } from 'Resources/AgentRuns';
import AgentApprovalsResource, { type AgentApproval } from 'Resources/AgentApprovals';
import AgentsResource, { type Agent } from 'Resources/Agents';
import { agentColor } from '../components/AgentChip';
import WorkspaceHomeCards from '../components/WorkspaceHomeCards';
import MessageThread from '../../Assistant/MessageThread';
import { type RunProgressStep } from '../../Assistant/RunSection';
import { usePlaybooksAccess } from 'App/components/hooks';
import DelegationDrawer from '../components/DelegationDrawer';
import type { DelegationData } from '../components/DelegationChip';
import useAgentRunEvents from './useAgentRunEvents';
import useAgentStream from './useAgentStream';
import messagesReducer from './messagesReducer';
import { consumeRunWorking } from '../Routines/playbookRunSignal';
import ChatInput from '../../Assistant/ChatInput';
import { buildDryRunPrompt } from '../Routines/WorkspaceEventHookConfig';
import type {
	AssistantMessage,
	MultiActionConfig,
	WriteProposal,
	EmailDraft,
	OfferDraft,
	Conversation,
	ChartSpec
} from '../../Assistant/types';
import { normalizeChartSpec } from '../../Assistant/ChartCard';
import { buildOptimisticAttachments, readAttachmentMetadata } from '../../Assistant/attachmentMessage';
import { setLastConversationId } from '../../Assistant/assistantStorage';
import {
	approvalToWriteProposal,
	approvalToMultiActionConfig,
	approvalToEmailDraft,
	approvalToOfferDraft
} from '../../Assistant/approvalAdapters';
import { notifyAIWorkspaceError } from '../notifyAIWorkspaceError';
import { buildPageContextString, type PageContext } from './buildPageContextString';

// First-open / seeded messages are sent as `[System: …]` prompts and hidden
// from the visible transcript. (The pre-WEB-7337 literal seeds are no longer
// matched here — those conversations have aged out; see WEB-7417.)
const isSystemMessage = (msg: { role: string; content: string }) =>
	msg.role === 'user' && msg.content.startsWith('[System:');

// Live step checklist for a Playbook/workflow run in flight on the open
// conversation (WEB-7897). Rendered below the run's user message.
type RunProgress = {
	conversationId: number;
	steps: RunProgressStep[];
};

type ScheduleDetailsState = {
	id?: number;
	prompt?: string;
	cronExpression?: string | null;
	label?: string;
	conversationId?: number;
	enabled?: boolean;
	nextRun?: string;
	agentId?: number | null;
};

type EventHookDetailsState = {
	id?: number;
	label?: string;
	entityType?: string | null;
	event?: 'insert' | 'update' | null;
	filters?: Array<{ field: string; operator: string; value?: string | number | boolean | null }>;
	prompt?: string;
	conversationId?: number;
	enabled?: boolean;
	agentId?: number | null;
};

type Props = {
	assistantName: string;
	avatarUrl: string | null;
	avatarColor?: string;
	personalAgentId?: number | null;
	conversationId: number | null;
	setConversationId: (id: number | null) => void;
	selfId?: number;
	// WEB-7409: true when the user already has the max number of starred
	// conversations. Disables the header star unless this conversation is one of
	// them (so it can still be un-starred).
	starLimitReached?: boolean;
	contextType?: string;
	contextId?: number;
	onRefreshSidebar?: () => void;
	onSelectConversation?: (conv: Conversation) => void;
	onNavigateAgent?: (agentId: number) => void;
	onOpenTask?: (taskId: number) => void;
	onOpenRoutine?: (routineId: number) => void;
	hideTopbar?: boolean;
	hideWelcome?: boolean;
	// A message to send automatically once the chat has mounted (e.g. the
	// navbar quick-search "Chat with AI" seed). Sent directly via handleSend
	// rather than the ws-pending-message event so it can't race the listener
	// on a cold mount. onInitialMessageSent lets the parent clear it so a
	// later fresh chat (remount) doesn't re-send it.
	initialMessage?: string | null;
	onInitialMessageSent?: () => void;
	// When set, the page the user is currently viewing is forwarded as ephemeral
	// per-turn context with the next message (built into turnContext). Not shown,
	// not persisted. Used by the Chrome side-panel embed (WEB-7101).
	pageContext?: PageContext | null;
	// Trusted, structured context (route + entity ids) for the in-app floating chat
	// (WEB-7113). Unlike pageContext, this does NOT trip the untrusted tool lockdown.
	trustedContext?: string | null;
};

const WorkspaceChat = ({
	assistantName,
	avatarUrl,
	avatarColor,
	personalAgentId,
	conversationId,
	setConversationId,
	selfId,
	starLimitReached,
	contextType = 'general',
	contextId,
	onRefreshSidebar,
	onSelectConversation,
	onNavigateAgent,
	onOpenTask,
	onOpenRoutine,
	hideTopbar,
	hideWelcome,
	initialMessage,
	onInitialMessageSent,
	pageContext,
	trustedContext
}: Props) => {
	// The shared transcript reducer (WEB-7957) — all optimistic message-list
	// mutations (ordering-aware text insertion, proposal coalescing, card
	// updates) live in Chat/messagesReducer.ts.
	const [messages, dispatchMessages] = useReducer(messagesReducer, [] as AssistantMessage[]);
	// WEB-6797 — agent_run metadata for this conversation, so the RunSection
	// header can show credits / duration / status per run. Two lookups because
	// run groups link to runs two different ways: assistant messages may carry
	// `agentRunId` (→ byId), but routine-fired runs link only via the trigger
	// `runTimestamp`/`runId` on the run's contextSnapshot (→ byMarker).
	const [runsById, setRunsById] = useState<Map<number, AgentRun>>(new Map());
	const [runsByMarker, setRunsByMarker] = useState<Map<string, AgentRun>>(new Map());
	// Bumped when a headless (Playbook/workflow) run finalizes — those never flip
	// `sending`, so the metadata effect below wouldn't otherwise refetch, and the
	// just-finished run's cost / duration / status wouldn't appear (WEB-7897).
	const [runMetaRefresh, setRunMetaRefresh] = useState(0);
	// Playbooks is soft-deploy gated: the run-as-turn UI (live step checklist,
	// persisted "Steps" record, "Running your routine…" banner) must not appear
	// unless AGENT_PLAYBOOKS is on, even if a conversation carries Playbook-run
	// data (WEB-7897).
	const playbooksEnabled = usePlaybooksAccess();
	// WEB-6800: delegation shown in the side-drawer (null = closed). Holds the full
	// DelegationData so the drawer can render a nested chat with the right child
	// conversationId, agent identity and title.
	const [delegation, setDelegation] = useState<DelegationData | null>(null);
	const [conversationTitle, setConversationTitle] = useState<string | null>(null);
	// The delegating (parent) conversation, when this is a delegated child. Drives
	// the topbar back-link in the child's full view (hidden in the drawer, which is
	// already opened from the parent).
	const [parentConversationId, setParentConversationId] = useState<number | null>(null);
	const [conversationStarred, setConversationStarred] = useState(false);
	const [scheduleDetails, setScheduleDetails] = useState<ScheduleDetailsState | null>(null);
	const [eventHookDetails, setEventHookDetails] = useState<EventHookDetailsState | null>(null);
	// When this conversation is pinned to a routine (scheduled / event / webhook),
	// the header shows a link back to its configuration. Resolved by reverse
	// lookup on `assistant_routines.conversation_id`, so it covers all three
	// trigger kinds regardless of the conversation's contextType.
	const [routineLink, setRoutineLink] = useState<{ id: number; label: string } | null>(null);
	// When the active conversation is pinned to a routine that targets a
	// specific agent (e.g. the post-meeting routine pointing at the post-
	// meeting agent), attribute the assistant bubbles to THAT agent rather
	// than the user's personal Jarvis. `null` => fall back to the global
	// assistant identity passed in via props.
	const [routineAgent, setRoutineAgent] = useState<{ name: string; avatarUrl: string | null; id: number } | null>(
		null
	);
	const conversationIdRef = useRef<number | null>(null);
	const lastLoadedConvId = useRef<number | null>(null);
	const agentStream = useAgentStream();
	const stopStream = useCallback(
		(id: number) => {
			const live = agentStream.stop(id);
			if (!live || !live.fullText.trim()) return;
			const assistantMetadata: Record<string, unknown> = { stopped: true };
			if (live.activeRunId) assistantMetadata.runId = live.activeRunId;
			dispatchMessages({
				type: 'insertAssistantText',
				message: {
					role: 'assistant',
					content: live.fullText,
					metadata: assistantMetadata
				}
			});
		},
		[agentStream]
	);
	const [userSending, setUserSending] = useState(false);
	const [loading, setLoading] = useState(!!conversationId);
	const [initialized, setInitialized] = useState(false);
	const [createdAgent, setCreatedAgent] = useState<{ agentId: number; name: string; updated?: boolean } | null>(null);
	const [allAgents, setAllAgents] = useState<Agent[]>([]);
	const contentRowRef = useRef<HTMLDivElement>(null);
	const scrollerRef = useRef<HTMLDivElement>(null);
	// Tracks whether the user is currently at (or within 80px of) the bottom
	// of the chat scroller. Updated on every scroll event. Read by the
	// autoscroll effect below so we only follow new content when the user
	// hasn't deliberately scrolled up to read something earlier.
	const isPinnedToBottomRef = useRef(true);
	const isInitialLoadRef = useRef(true);
	const prevUserIdRef = useRef<number | undefined>(selfId);

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;
		const onScroll = () => {
			isPinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
		};
		el.addEventListener('scroll', onScroll, { passive: true });
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		AgentsResource.list()
			.then(setAllAgents)
			.catch(err => notifyAIWorkspaceError(err, 'listAgents'));
	}, []);

	const [recentThread, setRecentThread] = useState<Conversation | null>(null);
	useEffect(() => {
		if (conversationId !== null) return;
		AssistantResource.listConversations()
			.then((convs: Conversation[]) => {
				const top = (convs || [])
					.filter(c => !c.contextType || c.contextType === 'general')
					.sort((a, b) => new Date(b.modDate).getTime() - new Date(a.modDate).getTime())[0];
				setRecentThread(top ?? null);
			})
			.catch(() => {
				/* Decorative "recent thread" hint; silent on failure. */
			});
	}, [conversationId]);

	const mentionAgents = useMemo(() => allAgents.map(a => ({ id: a.id, name: a.name })), [allAgents]);

	conversationIdRef.current = conversationId;

	const currentStream = agentStream.get(conversationId);
	const streamingText = currentStream?.fullText ?? '';
	const toolStatus = currentStream?.toolStatus ?? '';
	const activeRunId = currentStream?.activeRunId ?? null;
	const sending = !!currentStream;
	const thinkingText = currentStream?.thinking ?? '';

	// WEB-6797 — load agent_run metadata for the open conversation so RunSection
	// headers can show credits / duration / status. Refetch when a send finishes
	// (`sending` false) so a just-completed run's cost + terminal status appear.
	useEffect(() => {
		if (conversationId == null || sending) return;
		let cancelled = false;
		AgentRunsResource.list({ conversationId, limit: 200 })
			.then(res => {
				if (cancelled) return;
				setRunsById(new Map(res.data.map(r => [r.id, r] as [number, AgentRun])));
				const byMarker = new Map<string, AgentRun>();
				for (const r of res.data) {
					const trigger = (
						r.contextSnapshot as {
							triggeredByMessageMetadata?: { runTimestamp?: string; runId?: string };
						} | null
					)?.triggeredByMessageMetadata;
					// Last-wins so a group's header reflects the most recent run for
					// that trigger marker (retries share a runTimestamp).
					if (trigger?.runTimestamp) byMarker.set(trigger.runTimestamp, r);
					if (trigger?.runId) byMarker.set(trigger.runId, r);
				}
				setRunsByMarker(byMarker);
			})
			.catch(err => console.error('[WorkspaceChat] failed to load runs for header metadata', err));
		return () => {
			cancelled = true;
		};
	}, [conversationId, sending, runMetaRefresh]);

	// WEB-6797 — coarse provenance label from the conversation's nature. Routine
	// conversations (scheduled / event hook) share one provenance across all
	// their runs; plain chats show none (a user message is its own context).
	const provenance = useMemo<string | null>(() => {
		if (scheduleDetails) return T('assistant.runHeader.provenance.scheduled') || 'Scheduled';
		if (eventHookDetails) return T('assistant.runHeader.provenance.eventHook') || 'Event hook';
		if (contextType === 'meeting') return T('assistant.runHeader.provenance.meeting') || 'Meeting';
		return null;
	}, [scheduleDetails, eventHookDetails, contextType]);

	useEffect(() => {
		if (selfId && prevUserIdRef.current !== selfId) {
			prevUserIdRef.current = selfId;
			dispatchMessages({ type: 'set', messages: [] });
			agentStream.clearAll();
			setConversationId(null);
			setUserSending(false);
			setInitialized(false);
		}
	}, [selfId, agentStream, setConversationId]);

	const isRunBased = !!scheduleDetails || !!eventHookDetails;
	useEffect(() => {
		if (!isInitialLoadRef.current && !isPinnedToBottomRef.current) return;
		// Scroll the scroller directly — scrollIntoView on a sentinel also scrolls
		// every scrollable ancestor, permanently shifting the embeds' clipped boxes.
		const el = scrollerRef.current;
		el?.scrollTo({ top: el.scrollHeight, behavior: isInitialLoadRef.current ? 'instant' : 'smooth' });
		isInitialLoadRef.current = false;
	}, [messages, streamingText, thinkingText, isRunBased]);

	const handleMultiAction = useCallback((config: MultiActionConfig, runId: string | null) => {
		const metadata = runId ? { runId } : undefined;
		dispatchMessages({
			type: 'append',
			message: { role: 'assistant' as const, content: '', multiAction: config, metadata }
		});
	}, []);

	const handleMultiActionUpdate = useCallback((id: string, updates: Partial<MultiActionConfig>) => {
		// The card resolves the approval server-side (AgentApprovalsResource.approve/
		// reject) and the backend feeds the outcome back to the agent as a tool_result
		// so it continues naturally. We only update the card's local state here — no
		// synthetic follow-up message (that used to double up with the backend resume).
		dispatchMessages({ type: 'updateMultiAction', id, updates });
	}, []);

	const streamMessage = useCallback(
		async (
			convId: number,
			text: string,
			showUserMessage: boolean,
			opts?: {
				files?: File[];
				isScheduledRun?: boolean;
				runId?: string;
				regenerate?: boolean;
				turnContext?: string;
				trustedContext?: string;
			}
		) => {
			const files = opts?.files;
			const runTimestamp = opts?.isScheduledRun ? new Date().toISOString() : undefined;
			const runMetadata = opts?.isScheduledRun
				? { runMarker: true, runTimestamp, manual: true }
				: opts?.runId
				? { runId: opts.runId }
				: undefined;
			const streamRunId = opts?.isScheduledRun ? runTimestamp ?? null : opts?.runId ?? null;
			if (showUserMessage) {
				dispatchMessages({
					type: 'append',
					message: {
						role: 'user',
						content: text,
						...(await buildOptimisticAttachments(files)),
						metadata: runMetadata
					}
				});
			}
			const isActive = () => convId === conversationIdRef.current;

			await agentStream.sendSSE(
				convId,
				text,
				{
					contextType,
					contextId,
					files,
					isScheduledRun: opts?.isScheduledRun,
					runId: opts?.runId,
					runTimestamp: opts?.isScheduledRun ? runTimestamp : undefined,
					regenerate: opts?.regenerate,
					turnContext: opts?.turnContext,
					trustedContext: opts?.trustedContext,
					activeRunId: streamRunId,
					isActive
				},
				{
					onDone: live => {
						if (isActive() && (live.fullText.trim() || live.documents?.length)) {
							const delegationTrace = (live.delegations ?? []).map(d => ({
								name: 'delegateTask',
								input: JSON.stringify({ title: d.title }),
								output: JSON.stringify({ ok: true, ...d })
							}));
							const assistantMetadata =
								streamRunId || delegationTrace.length
									? {
											...(streamRunId ? { runId: streamRunId } : {}),
											...(delegationTrace.length ? { toolTrace: delegationTrace } : {})
									  }
									: undefined;
							dispatchMessages({
								type: 'insertAssistantText',
								message: {
									role: 'assistant',
									content: live.fullText,
									metadata: assistantMetadata,
									thinking: live.thinking || undefined,
									suggestedReplies: live.suggestedReplies,
									attachments: live.documents
								}
							});
						}
						if (opts?.isScheduledRun) {
							AssistantRoutinesResource.getRoutines()
								.then(routines => {
									setScheduleDetails(prev => {
										if (!prev?.id) return prev;
										const routine = routines.find(r => r.id === prev.id);
										return routine ?? prev;
									});
								})
								.catch(() => {
									/* Stream callback — fires per run event; silent to avoid toast spam. */
								});
						}
					},
					onError: error => {
						if (!isActive()) return;
						const message =
							error === 'api_key_not_configured'
								? T('assistant.errorApiKey')
								: T('assistant.errorOccurred');
						const assistantMetadata = streamRunId ? { runId: streamRunId } : undefined;
						dispatchMessages({
							type: 'insertAssistantText',
							message: {
								role: 'assistant',
								content: message,
								metadata: assistantMetadata
							}
						});
					},
					onPanel: panel => {
						// The agent emits the chart spec via the showChart tool; render
						// it inline as a card-only assistant message so it sits above
						// the streaming text reply that follows.
						const params = (panel as { params?: unknown })?.params;
						const spec = normalizeChartSpec(params);
						dispatchMessages({ type: 'append', message: { role: 'assistant', content: '', chart: spec } });
					},
					onImages: urls => {
						dispatchMessages({ type: 'setLastUserImages', urls });
					},
					onAttachments: serverAttachments => {
						const nonImage = serverAttachments.filter(
							a => !(a.mimeType || '').toLowerCase().startsWith('image/')
						);
						if (nonImage.length === 0) return;
						dispatchMessages({ type: 'setLastUserAttachments', attachments: nonImage });
					},
					onMultiAction: config => {
						handleMultiAction(config as MultiActionConfig, streamRunId);
					},
					onWriteProposal: proposal => {
						const rawApprovalId = (proposal as { approvalId?: number | null }).approvalId;
						const newItem: WriteProposal = {
							entity: String(proposal.entity ?? ''),
							action: proposal.action as WriteProposal['action'],
							data: (proposal.data as Record<string, unknown>) ?? {},
							status: 'pending',
							approvalId: typeof rawApprovalId === 'number' ? rawApprovalId : null,
							matchingContacts:
								typeof proposal.matchingContacts === 'number' ? proposal.matchingContacts : undefined
						};
						dispatchMessages({ type: 'addWriteProposal', proposal: newItem, runId: streamRunId });
					},
					onEmailDraft: draft => {
						const metadata = streamRunId ? { runId: streamRunId } : undefined;
						dispatchMessages({
							type: 'append',
							message: {
								role: 'assistant',
								content: '',
								metadata,
								emailDraft: {
									contactIds: draft.contactIds,
									subject: draft.subject,
									body: draft.body,
									ccContactIds: draft.ccContactIds,
									clientId: draft.clientId,
									attachments: draft.attachments,
									status: 'pending',
									approvalId: draft.approvalId ?? null
								}
							}
						});
					},
					onOfferDraft: draft => {
						const metadata = streamRunId ? { runId: streamRunId } : undefined;
						dispatchMessages({
							type: 'append',
							message: {
								role: 'assistant',
								content: '',
								metadata,
								offerDraft: {
									clientId: draft.clientId,
									contactId: draft.contactId,
									products: draft.products,
									currency: draft.currency,
									validUntil: draft.validUntil,
									description: draft.description,
									notes: draft.notes,
									status: 'pending',
									approvalId: draft.approvalId ?? null
								}
							}
						});
					},
					onAgentCreated: info => {
						setCreatedAgent(info);
					},
					onIdentityUpdated: identity => {
						window.dispatchEvent(new CustomEvent('ws-identity-updated', { detail: identity }));
					}
				}
			);
		},
		[handleMultiAction, agentStream, contextType, contextId]
	);

	// Approving/rejecting a write proposal resolves the approval server-side (the
	// card calls AgentApprovalsResource.approve/reject), and the backend feeds the
	// outcome back to the agent as a tool_result so it continues naturally. So we
	// only update the card's local state here — no synthetic follow-up message
	// (that used to double up with the backend resume).
	const handleWriteProposalUpdate = useCallback((index: number, update: Partial<WriteProposal>) => {
		dispatchMessages({ type: 'updateWriteProposal', index, update });
	}, []);

	const handleWriteProposalGroupItemUpdate = useCallback(
		(messageIndex: number, itemIndex: number, update: Partial<WriteProposal>) => {
			dispatchMessages({ type: 'updateWriteProposalGroupItem', messageIndex, itemIndex, update });
		},
		[]
	);

	const handleApprovalsGroupEmailItemUpdate = useCallback(
		(messageIndex: number, itemIndex: number, update: Partial<EmailDraft>) => {
			dispatchMessages({ type: 'updateWriteProposalGroupEmailItem', messageIndex, itemIndex, update });
		},
		[]
	);

	// Like write proposals, email drafts resolve their approval server-side (the
	// card calls AgentApprovalsResource.approve/reject) and the backend resumes the
	// agent via tool_result — so we only update local card state here.
	const handleEmailDraftUpdate = useCallback((index: number, update: Partial<EmailDraft>) => {
		dispatchMessages({ type: 'updateEmailDraft', index, update });
	}, []);

	// Offer drafts resolve their approval server-side (the card calls
	// AgentApprovalsResource.approve/reject), so we only update local card state.
	const handleOfferDraftUpdate = useCallback((index: number, update: Partial<OfferDraft>) => {
		dispatchMessages({ type: 'updateOfferDraft', index, update });
	}, []);

	useEffect(() => {
		if (!initialized) setInitialized(true);
	}, [initialized]);

	// Read page context from a ref inside handleSend so the callback identity stays stable.
	// pageContext changes on every tab switch (the extension pushes fresh context), and
	// putting it in handleSend's deps would needlessly churn the ws-pending-message listener.
	const pageContextRef = useRef(pageContext);
	useEffect(() => {
		pageContextRef.current = pageContext;
	}, [pageContext]);

	const trustedContextRef = useRef(trustedContext);
	useEffect(() => {
		trustedContextRef.current = trustedContext;
	}, [trustedContext]);

	const handleSend = useCallback(
		async (text: string, files?: File[]) => {
			if ((!text.trim() && (!files || files.length === 0)) || userSending) return;
			setUserSending(true);
			try {
				let convId = conversationId;
				if (!convId) {
					const result = await AssistantResource.createConversation(contextType, contextId);
					convId = result.id;
					lastLoadedConvId.current = convId;
					setConversationId(convId);
					if (contextType === 'general') setLastConversationId(convId);
				}
				// In routine-pinned conversations every top-level send is a new
				// run, not an inline reply. Tag it so the user message gets a
				// runMarker + runTimestamp and the stream's activeRunId matches,
				// otherwise MessageThread renders the streaming block outside
				// any RunSection (gated `!activeRunId`) and the test run looks
				// composer-less while executing.
				const startNewRun = !!scheduleDetails || !!eventHookDetails;
				const turnContext = buildPageContextString(pageContextRef.current ?? null) ?? undefined;
				const trusted = trustedContextRef.current ?? undefined;
				await streamMessage(convId, text, true, {
					files,
					isScheduledRun: startNewRun,
					turnContext,
					trustedContext: trusted
				});
			} finally {
				setUserSending(false);
			}
		},
		[
			userSending,
			conversationId,
			contextType,
			contextId,
			streamMessage,
			setConversationId,
			scheduleDetails,
			eventHookDetails
		]
	);

	const handleSendHidden = useCallback(
		async (text: string) => {
			if (!text.trim() || userSending) return;
			setUserSending(true);
			try {
				let convId = conversationId;
				if (!convId) {
					const result = await AssistantResource.createConversation(contextType, contextId);
					convId = result.id;
					lastLoadedConvId.current = convId;
					setConversationId(convId);
					if (contextType === 'general') setLastConversationId(convId);
				}
				await streamMessage(convId, text, false);
			} finally {
				setUserSending(false);
			}
		},
		[userSending, conversationId, contextType, contextId, streamMessage, setConversationId]
	);

	// Queue ws-pending-message events that arrive while a conversation is still
	// loading. Without this, a test run navigating to an existing routine
	// conversation would race loadConversation: handleSend appends the user
	// message + stream, then loadConversation's atomic swap wipes them.
	const pendingDispatchRef = useRef<{ text: string; files?: File[]; hidden?: boolean } | null>(null);
	useEffect(() => {
		const handler = (e: Event) => {
			const { text, files, hidden } = (e as CustomEvent<{ text: string; files?: File[]; hidden?: boolean }>)
				.detail;
			if (!text) return;
			if (loading) {
				pendingDispatchRef.current = { text, files, hidden };
				return;
			}
			if (hidden) handleSendHidden(text);
			else handleSend(text, files);
		};
		window.addEventListener('ws-pending-message', handler);
		return () => window.removeEventListener('ws-pending-message', handler);
	}, [handleSend, handleSendHidden, loading]);

	useEffect(() => {
		if (loading) return;
		const pending = pendingDispatchRef.current;
		if (!pending) return;
		pendingDispatchRef.current = null;
		if (pending.hidden) handleSendHidden(pending.text);
		else handleSend(pending.text, pending.files);
	}, [loading, handleSend, handleSendHidden]);

	// Send a seeded initial message (e.g. the navbar quick-search query) once,
	// after the chat has mounted and any existing conversation finished loading.
	// Direct handleSend call — no event/timer — so it can't race on cold mount.
	const initialMessageSentRef = useRef(false);
	useEffect(() => {
		if (!initialMessage || initialMessageSentRef.current || loading) return;
		initialMessageSentRef.current = true;
		handleSend(initialMessage);
		onInitialMessageSent?.();
	}, [initialMessage, loading, handleSend, onInitialMessageSent]);

	const loadConversation = useCallback(
		async (convId: number) => {
			try {
				setLoading(true);
				isInitialLoadRef.current = true;

				const convPromise = AssistantResource.getConversation(convId);
				const routinesPromise = AssistantRoutinesResource.getRoutines();
				const [full, allRoutines] = await Promise.all([convPromise, routinesPromise]);

				let nextSchedule: ScheduleDetailsState | null = null;
				let nextHook: EventHookDetailsState | null = null;
				if (full.contextType === 'scheduled' && full.contextId) {
					nextSchedule = allRoutines.find(r => r.id === full.contextId && !!r.cronExpression) ?? null;
				} else if (full.contextType === 'event_hook' && full.contextId) {
					nextHook = allRoutines.find(r => r.id === full.contextId && !!r.entityType) ?? null;
				}
				// WEB-6796: also detect routine-pinned conversations via reverse
				// lookup. Routines opened from the sidebar (WEB-6795) may have a
				// generic contextType on the conversation row but still own the
				// conversation via assistant_routines.conversation_id. Treat them
				// as run-based so RunSection grouping kicks in.
				if (!nextSchedule && !nextHook) {
					const ownedByRoutine = allRoutines.find(r => r.conversationId === full.id);
					if (ownedByRoutine) {
						if (ownedByRoutine.cronExpression) nextSchedule = ownedByRoutine;
						else if (ownedByRoutine.entityType) nextHook = ownedByRoutine;
					}
				}
				// Header "View routine" link — resolve the owning routine for any
				// trigger kind (webhook routines are neither schedule nor hook, so
				// fall back to the reverse lookup on conversation_id).
				const owningRoutine =
					nextSchedule ?? nextHook ?? allRoutines.find(r => r.conversationId === full.id) ?? null;
				const nextRoutineLink =
					owningRoutine && typeof owningRoutine.id === 'number'
						? { id: owningRoutine.id, label: owningRoutine.label || T('assistant.routine') || 'Routine' }
						: null;

				type RawMessageMeta = {
					imageUrls?: string[];
					attachments?: AssistantMessage['attachments'];
					runMarker?: boolean;
					runId?: string;
					approvalIds?: number[];
					charts?: unknown[];
					toolTrace?: unknown[];
					delegationCompletion?: unknown;
					suggestedReplies?: string[];
					source?: string;
					headlessRun?: boolean;
				};
				const rawMessages = (full.messages || []).filter(
					(m: { role: string; content: string }) =>
						(m.role === 'user' || m.role === 'assistant') && !isSystemMessage(m)
				);
				// Suggested-reply pills are only meaningful on the latest assistant
				// turn (MessageThread renders them only for the last message), so we
				// restore them solely there — older turns stay clean.
				let lastAssistantIdx = -1;
				rawMessages.forEach((m: { role: string }, i: number) => {
					if (m.role === 'assistant') lastAssistantIdx = i;
				});
				const perMessageApprovalIds: number[][] = rawMessages.map((m: { metadata?: unknown }) => {
					try {
						const meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata as string) : m.metadata;
						const ids = (meta as RawMessageMeta)?.approvalIds;
						return Array.isArray(ids) ? ids.filter(n => typeof n === 'number') : [];
					} catch {
						return [];
					}
				});
				const allApprovalIds = Array.from(new Set(perMessageApprovalIds.flat()));
				const approvalsById = new Map<number, AgentApproval>();
				if (allApprovalIds.length > 0) {
					try {
						const fetched = await AgentApprovalsResource.getMany(allApprovalIds);
						fetched.forEach(a => approvalsById.set(a.id, a));
					} catch {
						// non-fatal — cards just won't reconstruct on this reload
					}
				}

				const nextMessages: AssistantMessage[] = rawMessages.flatMap(
					(
						m: { id: number; role: string; content: string; regDate: string; metadata?: unknown },
						idx: number
					) => {
						let images: string[] | undefined;
						let attachments: AssistantMessage['attachments'];
						let metadata: Record<string, unknown> | undefined;
						let chartSpecs: ChartSpec[] = [];
						let suggestedReplies: string[] | undefined;
						try {
							const meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata as string) : m.metadata;
							const typed = meta as RawMessageMeta;
							// Shared with the Pages chat so both reload paths agree on the
							// attachment shape; the fields below are specific to this chat.
							({ images, attachments } = readAttachmentMetadata(meta));
							// Preserve metadata for run-grouped messages (runId/runMarker)
							// AND for any message carrying a toolTrace (the delegation chip
							// is extracted from metadata.toolTrace) OR a delegationCompletion
							// marker (the delegated-result banner). Plain chat delegations
							// have neither runId nor runMarker (WEB-6800).
							if (
								typed?.runMarker ||
								typed?.runId ||
								Array.isArray(typed?.toolTrace) ||
								typed?.delegationCompletion
							) {
								metadata = meta as Record<string, unknown>;
							}
							// WEB-6916: rebuild the chart card(s) the agent emitted via
							// showChart. They render as their own card-only assistant
							// messages above the text reply, mirroring the live onPanel path.
							if (Array.isArray(typed?.charts)) chartSpecs = typed.charts.map(c => normalizeChartSpec(c));
							// Restore suggested-reply pills only on the most recent
							// assistant turn (see lastAssistantIdx above).
							if (idx === lastAssistantIdx && typed?.suggestedReplies?.length)
								suggestedReplies = typed.suggestedReplies;
							// The post-meeting agent kicks off each run with a generated
							// prompt (meeting metadata + transcript context) sent as a
							// `user` message. That's internal plumbing, not something the
							// rep wrote — hide it so the run opens on the agent's output
							// (summary + drafts). It MUST stay in the array: groupMessagesByRun
							// uses the user message to delimit run boundaries, and RunSection
							// skips messages flagged `hidden` at render time.
							if (m.role === 'user' && typed?.source === 'post_meeting') {
								metadata = { ...(metadata ?? {}), hidden: true };
							}
						} catch {
							/* no metadata */
						}

						const approvals = perMessageApprovalIds[idx]
							.map(id => approvalsById.get(id))
							.filter((a): a is AgentApproval => !!a);

						const writeApprovals = approvals.filter(
							a =>
								a.action === 'crmMutation' || a.action === 'proposeCrmChange' || a.action === 'writeCRM'
						);
						const multiApprovals = approvals.filter(a => a.action === 'multiAction');
						const emailApprovals = approvals.filter(a => a.action === 'sendEmail');
						const offerApprovals = approvals.filter(a => a.action === 'draftOffer');

						const base: AssistantMessage = {
							id: m.id,
							role: m.role as 'user' | 'assistant',
							content: m.content,
							regDate: m.regDate,
							images,
							attachments,
							metadata,
							suggestedReplies
						};

						// Chart cards sit above the text reply, same order as live
						// (onPanel pushes the chart, then the text lands after it).
						const chartMessages: AssistantMessage[] = chartSpecs.map(chart => ({
							role: 'assistant' as const,
							content: '',
							chart,
							regDate: m.regDate,
							metadata
						}));

						// Offer drafts render as their own standalone cards (line items +
						// total), not inside the write/email approvals group.
						const offerMessages: AssistantMessage[] = offerApprovals.map(a => ({
							role: 'assistant' as const,
							content: '',
							regDate: m.regDate,
							metadata,
							offerDraft: approvalToOfferDraft(a)
						}));

						if (
							writeApprovals.length === 0 &&
							multiApprovals.length === 0 &&
							emailApprovals.length === 0 &&
							offerApprovals.length === 0
						) {
							return [...chartMessages, base];
						}

						// Attach all present approval cards directly to the same message.
						// Writes AND emails go into a single unified approvals group so
						// a post-meeting completion (typically 2 task drafts + 1 email
						// draft) shows up under one "Proposals to review (3)" header
						// instead of fragmented sections. multiAction stays separate
						// because it's a self-contained editor card.
						if (multiApprovals.length > 0) {
							base.multiAction = approvalToMultiActionConfig(multiApprovals[0]);
						}
						if (writeApprovals.length > 0 || emailApprovals.length > 0) {
							base.writeProposalGroup = {
								items: [
									...writeApprovals.map(a => ({
										kind: 'write' as const,
										proposal: approvalToWriteProposal(a)
									})),
									...emailApprovals.map(a => ({
										kind: 'email' as const,
										draft: approvalToEmailDraft(a)
									}))
								]
							};
						}
						return [...chartMessages, base, ...offerMessages];
					}
				);

				// Routine-pinned conversations should be attributed to the
				// routine's target agent rather than the user's personal
				// assistant. Look up the agent (best-effort — a failure here
				// just falls back to the global assistant identity).
				const routineForAgent = nextHook ?? nextSchedule;
				const routineAgentId = routineForAgent?.agentId != null ? routineForAgent.agentId : null;
				let resolvedAgent: { id: number; name: string; avatarUrl: string | null } | null = null;
				if (routineAgentId != null) {
					try {
						const agent = await AgentsResource.getOne(routineAgentId);
						if (agent?.name) {
							resolvedAgent = {
								id: routineAgentId,
								name: agent.name,
								avatarUrl: agent.avatarUrl ?? null
							};
						}
					} catch {
						/* fall back to props */
					}
				}

				unstable_batchedUpdates(() => {
					// Stale-response guard: if the user navigated away while this load
					// was in flight (e.g. clicked "New chat" in the sidebar, dropping
					// conversationId to null), don't apply the results. Re-adopting the
					// old id via setConversationId would hijack the navigation and leave
					// the chat stuck on an empty, message-less state — the intermittent
					// "blank page" when opening a new chat from inside a conversation.
					if (conversationIdRef.current !== convId) {
						setLoading(false);
						return;
					}
					setConversationId(convId);
					if (contextType === 'general') setLastConversationId(convId);
					dispatchMessages({ type: 'set', messages: nextMessages });
					setConversationTitle(full.title || null);
					setParentConversationId(
						typeof full.parentConversationId === 'number' ? full.parentConversationId : null
					);
					setConversationStarred(!!full.starred);
					setScheduleDetails(nextSchedule);
					setEventHookDetails(nextHook);
					setRoutineLink(nextRoutineLink);
					setRoutineAgent(resolvedAgent);
					setUserSending(false);
					setLoading(false);
				});
			} catch {
				setLoading(false);
			}
		},
		[setConversationId]
	);

	// Drop the routine link the moment the conversation changes so a freshly
	// created chat can't briefly show the previous routine's link; loadConversation
	// re-derives it for routine-pinned conversations.
	useEffect(() => {
		setRoutineLink(null);
	}, [conversationId]);

	// WEB-7824: a "working" state for a Playbook/workflow run in flight on the
	// open conversation. A workflow run doesn't stream text, so the normal
	// streaming bubble never shows — instead we surface a lightweight banner from
	// click (optimistic `ws-run-working` event) until Pusher `runStarted`, through
	// to `runFinalized` (which reloads the conversation → the summary appears).
	const [workingConvId, setWorkingConvId] = useState<number | null>(null);

	useEffect(() => {
		// New/empty chat — nothing to load. Clear any stale `loading` flag: when
		// "New chat" is clicked from inside a conversation, the chatKey remount can
		// briefly mount with the previous conversationId (the remount and the URL
		// reset land in separate renders), so `loading` initialises to true via
		// useState(!!conversationId). Once conversationId flips to null this effect
		// must turn it back off, otherwise isEmptyChat stays false and the welcome
		// home never renders — the intermittent blank page.
		if (!conversationId) {
			setLoading(false);
			return;
		}
		if (!initialized) return;
		if (conversationId === lastLoadedConvId.current) return;
		lastLoadedConvId.current = conversationId;
		// Nothing is cleared here on purpose — the previous conversation's title
		// and messages stay on screen until loadConversation's
		// unstable_batchedUpdates swaps everything in one atomic render. Clearing
		// title/messages here would force an intermediate blank render and
		// produce a visible header flicker + jump-to-bottom on the messages.
		setLoading(true);
		loadConversation(conversationId);
		// WEB-7818: if the user just triggered a test run for this conversation,
		// show the "working" banner right away (reliable across the navigation,
		// unlike the window event which can beat this component's listener).
		if (conversationId != null && consumeRunWorking(conversationId)) {
			setWorkingConvId(conversationId);
		}
	}, [conversationId, initialized, loadConversation]);

	useEffect(() => {
		const unsub = Tools?.$rootScope?.$on(
			'userPrivate.agent.conversationMessage',
			(_e: unknown, data: { conversationId: number }) => {
				if (data.conversationId === conversationIdRef.current && !agentStream.get(data.conversationId)) {
					loadConversation(data.conversationId);
				}
			}
		);
		return () => unsub?.();
	}, [loadConversation, agentStream]);

	// Live-stream HEADLESS runs (delegations / routines) we're viewing — the worker
	// run publishes agent.runEvent over Pusher (keyed by runId; runStarted carries
	// conversationId), so we render it live without an SSE request. Guarded so it
	// never collides with this user's own SSE stream (source 'sse').
	const runConvMapRef = useRef<Map<number, number>>(new Map());
	// Runs owned by THIS client's own SSE request. The backend mirrors every SSE
	// stream event into Pusher agent.runEvent (fire-and-forget, so they lag behind
	// the SSE). The `source === 'sse'` guard below only holds while the SSE stream
	// exists — once onDone removes it, straggler Pusher copies of our own text
	// chunks would otherwise spawn a phantom "pusher" stream (a duplicate bubble
	// with just the tail of the reply). Marking the run as local at runStarted —
	// while the SSE stream provably exists — lets us drop its events forever.
	const localRunsRef = useRef<Set<number>>(new Set());
	// Backstop: events for an already-finalized run can never start or extend a
	// stream (a straggler arriving after runFinalized would create a phantom that
	// nothing ever cleans up). Session-lived; numbers only, so growth is trivial.
	const finalizedRunsRef = useRef<Set<number>>(new Set());

	useEffect(() => {
		const onWorking = (e: Event) => {
			const cid = (e as CustomEvent<{ conversationId?: number }>).detail?.conversationId;
			if (typeof cid === 'number') setWorkingConvId(cid);
		};
		window.addEventListener('ws-run-working', onWorking);
		return () => window.removeEventListener('ws-run-working', onWorking);
	}, []);

	// WEB-7897: live step checklist for a Playbook/workflow run in flight on the
	// open conversation. Seeded by the `run_plan` event and ticked off as
	// `tool_use`/`tool_result` (and `plan_step_*`) events arrive, so the run's
	// steps render as visual guidance below its user message. Cleared on finalize.
	const [runProgress, setRunProgress] = useState<RunProgress | null>(null);

	useAgentRunEvents({
		onRunStarted: data => {
			if (data.conversationId != null) {
				runConvMapRef.current.set(data.runId, data.conversationId);
				if (agentStream.get(data.conversationId)?.source === 'sse') {
					localRunsRef.current.add(data.runId);
				} else if (playbooksEnabled) {
					// A run we didn't start (worker-dispatched Playbook/test run) →
					// show the working banner for its conversation. Gated: the banner is
					// Playbook UI (WEB-7897).
					setWorkingConvId(data.conversationId);
				}
			}
		},
		onRunEvent: data => {
			if (localRunsRef.current.has(data.runId) || finalizedRunsRef.current.has(data.runId)) return;
			// Prefer the conversationId carried on the event (robust when we
			// subscribed after the run started and missed runStarted); fall back
			// to the runStarted-built map.
			const cid = data.conversationId ?? runConvMapRef.current.get(data.runId) ?? null;
			if (cid == null || cid !== conversationIdRef.current) return;
			if (agentStream.get(cid)?.source === 'sse') return; // local request owns this conversation

			// Record every event — stream content folds into the live buffer, and
			// non-stream events (run_plan, tool_use, …) still advance the gap-free
			// frontier so out-of-order Pusher delivery can't stall the text.
			const streamText = (data.payload as { text?: unknown } | null)?.text;
			agentStream.ingestRunEvent(cid, {
				runId: data.runId,
				seq: data.seq,
				eventType: data.eventType,
				text: typeof streamText === 'string' ? streamText : null
			});

			// WEB-7897: Playbook/workflow step progress. These events carry no text —
			// they drive the in-turn checklist below the run's user message, not a
			// streaming bubble. `run_plan` seeds the ordered steps; tool_use /
			// plan_step_started mark a step running; tool_result / plan_step_done mark
			// it done. Handled (and returned) before the text-stream path below.
			// Gated on the flag so Playbook run progress never renders flag-off.
			if (playbooksEnabled) {
				const p = (data.payload ?? {}) as {
					steps?: Array<{
						id: string;
						kind: string;
						tool?: string;
						args?: Record<string, unknown>;
						prompt?: unknown;
					}>;
					stepId?: unknown;
					args?: Record<string, unknown>;
					resultPreview?: { count?: number; text?: string };
				};
				if (data.eventType === 'run_plan' && Array.isArray(p.steps)) {
					setRunProgress({
						conversationId: cid,
						steps: p.steps.map(s => ({
							id: s.id,
							kind: s.kind,
							tool: s.tool,
							args: s.args,
							prompt: s.prompt,
							status: 'pending'
						}))
					});
					return;
				}
				if (typeof p.stepId === 'string') {
					const running = data.eventType === 'tool_use' || data.eventType === 'plan_step_started';
					const done = data.eventType === 'tool_result' || data.eventType === 'plan_step_done';
					if (running || done) {
						const stepId = p.stepId;
						const resolvedArgs = p.args;
						const preview = p.resultPreview;
						setRunProgress(prev =>
							prev && prev.conversationId === cid
								? {
										...prev,
										steps: prev.steps.map(s =>
											s.id === stepId
												? {
														...s,
														status: done ? 'done' : 'running',
														// tool_use carries the resolved args (real params);
														// keep the template args otherwise.
														args: resolvedArgs ?? s.args,
														resultPreview: preview ?? s.resultPreview
												  }
												: s
										)
								  }
								: prev
						);
						return;
					}
				}
			}
		},
		onRunFinalized: data => {
			finalizedRunsRef.current.add(data.runId);
			const cid = data.conversationId ?? runConvMapRef.current.get(data.runId) ?? null;
			runConvMapRef.current.delete(data.runId);
			if (cid != null) setWorkingConvId(prev => (prev === cid ? null : prev));
			// The run is done — drop its step checklist; the reload below surfaces the
			// persisted result in its place (WEB-7897).
			if (cid != null) setRunProgress(prev => (prev?.conversationId === cid ? null : prev));
			// Refetch run metadata so the finished run's cost / duration / "Klar"
			// status appear on its header (WEB-7897).
			setRunMetaRefresh(k => k + 1);
			if (localRunsRef.current.delete(data.runId)) return; // own SSE run — onDone already handled the UI
			if (cid == null) return;
			const live = agentStream.get(cid);
			if (live && live.source === 'pusher') {
				// assistant_message has no thinking column — thinking is streamed, not
				// persisted (the SSE path keeps it in client state, see onDone). Capture
				// it before endStream so the server reload below doesn't drop the
				// reasoning we just streamed and leave the thinking block blank.
				const streamedThinking = live.thinking || undefined;
				agentStream.end(cid);
				// The conversationMessage refetch is skipped while a live stream exists,
				// so load the final persisted message explicitly now.
				if (cid === conversationIdRef.current) {
					loadConversation(cid)
						.then(() => {
							if (!streamedThinking || conversationIdRef.current !== cid) return;
							// Re-attach the streamed reasoning to the reloaded assistant
							// message, mirroring the SSE path.
							dispatchMessages({ type: 'attachThinkingToLastAssistant', thinking: streamedThinking });
						})
						.catch(err => console.error('[WorkspaceChat] reload after run finalize failed:', err));
				}
			} else if (!live && cid === conversationIdRef.current) {
				// A Playbook/workflow run doesn't stream text, so there's no live bubble
				// to swap here — but it just persisted its summary message. Reload so the
				// finished run surfaces as a new run group and the working banner (cleared
				// above) is replaced by real content. Without this, a deterministic test
				// run leaves the conversation visually unchanged until a manual refresh.
				loadConversation(cid).catch(err =>
					console.error('[WorkspaceChat] reload after workflow run finalize failed:', err)
				);
			}
		}
	});

	const now = new Date();
	const hour = now.getHours();
	const greetKey =
		hour < 5
			? 'assistant.greetNight'
			: hour < 12
			? 'assistant.greetMorning'
			: hour < 18
			? 'assistant.greetAfternoon'
			: 'assistant.greetEvening';
	const greetFallback = hour < 5 ? 'Night' : hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening';
	const greet = T(greetKey) || greetFallback;
	const timeOfDay = greetFallback;
	const dateLabel = now.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
	// The "Ask" home page is the empty state for a brand-new chat
	// (conversationId still null). For an existing conversation that just
	// happens to have no messages yet — a routine that hasn't run, a
	// hydrated `agent_run` shell with no comments — render the regular
	// MessageThread (empty), not the home page.
	const isEmptyChat =
		conversationId === null && messages.length === 0 && !sending && !userSending && !loading && !hideWelcome;

	const brandInitials = useMemo(() => {
		const words = (assistantName || '').trim().split(/\s+/).filter(Boolean);
		if (words.length === 0) return 'A';
		if (words.length === 1) return words[0].charAt(0).toUpperCase();
		return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
	}, [assistantName]);

	// Can't star a new (not-yet-starred) conversation once the cap is hit; an
	// already-starred one stays toggleable so it can be un-starred.
	const starDisabled = !conversationStarred && !!starLimitReached;

	const handleStarChat = () => {
		if (!conversationId) return;
		const next = !conversationStarred;
		// Safety net for the disabled button: don't let starring exceed the cap.
		if (next && starLimitReached) return;
		setConversationStarred(next);
		AssistantResource.starConversation(conversationId, next)
			.then(() => {
				onRefreshSidebar?.();
			})
			.catch(err => {
				setConversationStarred(!next);
				notifyAIWorkspaceError(err, 'starConversation');
			});
	};

	return (
		<>
			{!hideTopbar && !isEmptyChat && (
				<div className="ws-topbar">
					<div>
						<h1>{conversationTitle || (loading && conversationId ? ' ' : T('assistant.newChat'))}</h1>
						<div className="ws-sub">
							{timeOfDay} {'\u00b7'} {dateLabel}
						</div>
					</div>
					<div className="ws-topbar-actions">
						{/* Routine link: this conversation is pinned to a scheduled /
						    event / webhook routine — jump to its configuration. */}
						{routineLink && onOpenRoutine && (
							<button
								type="button"
								className="ws-topbar-parent-link"
								onClick={() => onOpenRoutine(routineLink.id)}
								title={T('assistant.viewRoutineHint') || 'Open this routine’s configuration'}
							>
								<Repeat style={{ width: 12, height: 12, flexShrink: 0 }} />
								<span>{T('assistant.viewRoutine') || 'View routine'}</span>
							</button>
						)}
						{/* Child->parent link: standalone child view only. In the drawer
						    (opened from the parent) it is redundant, and the topbar is
						    hidden there anyway. */}
						{parentConversationId != null && (
							<button
								type="button"
								className="ws-topbar-parent-link"
								onClick={() => {
									setConversationId(parentConversationId);
									setLastConversationId(parentConversationId);
								}}
							>
								<ArrowLeft style={{ width: 12, height: 12, flexShrink: 0 }} />
								<span>{T('assistant.backToParentConversation') || 'Back to main conversation'}</span>
							</button>
						)}
						{conversationId && (
							<button
								type="button"
								className={`ws-topbar-star ${conversationStarred ? 'ws-topbar-starred' : ''}`}
								title={starDisabled ? T('assistant.starLimitReached') : T('assistant.starred')}
								disabled={starDisabled}
								onClick={handleStarChat}
							>
								<Star
									style={{
										width: 16,
										height: 16,
										fill: conversationStarred ? 'currentColor' : 'none'
									}}
								/>
							</button>
						)}
					</div>
				</div>
			)}

			<div className="ws-content-row" ref={contentRowRef}>
				<div className="ws-chat-col">
					<div className={`ws-chat-body ${isEmptyChat ? 'ws-chat-body-welcome' : ''}`}>
						<div
							ref={scrollerRef}
							style={{ flex: 1, overflow: isEmptyChat ? 'hidden' : 'auto', width: '100%' }}
						>
							<div className="ws-chat-body-inner">
								{isEmptyChat && (
									<div className="ws-chat-welcome">
										<div className="ws-home-hero">
											<div
												className="ws-empty-hero"
												style={
													!avatarUrl && personalAgentId
														? { background: agentColor(personalAgentId) }
														: undefined
												}
											>
												{avatarUrl ? <img src={avatarUrl} alt="" /> : brandInitials}
											</div>
											<div className="ws-empty-greeting">
												{greet} {'\u00b7'} {dateLabel}
											</div>
											<h1>{T('assistant.howCanIHelp')}</h1>
										</div>
										<div className="ws-chat-starters">
											<WorkspaceHomeCards onSendPrompt={handleSendHidden} />
										</div>
									</div>
								)}
								{playbooksEnabled &&
									workingConvId != null &&
									workingConvId === conversationId &&
									runProgress?.conversationId !== conversationId && (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: 8,
												margin: '4px 0 12px',
												padding: '10px 14px',
												borderRadius: 10,
												background: 'var(--ws-bg-2, #f6f7f9)',
												fontSize: 13,
												color: 'var(--ws-ink-3, #5b6472)'
											}}
										>
											<Loader2 className="ws-spin" style={{ width: 15, height: 15 }} />
											<span>{T('assistant.playbook.runWorking') || 'Running your routine…'}</span>
										</div>
									)}
								<MessageThread
									messages={messages}
									streamingText={streamingText}
									streamingThinking={thinkingText}
									conversationId={conversationId}
									playbooksEnabled={playbooksEnabled}
									runProgress={
										playbooksEnabled && runProgress?.conversationId === conversationId
											? runProgress.steps
											: undefined
									}
									avatarUrl={routineAgent?.avatarUrl ?? avatarUrl}
									avatarColor={routineAgent ? agentColor(routineAgent.id) : avatarColor}
									assistantName={routineAgent?.name ?? assistantName}
									runBased={!!scheduleDetails || !!eventHookDetails}
									schedulePrompt={scheduleDetails?.prompt ?? eventHookDetails?.prompt ?? null}
									onStop={conversationId !== null ? () => stopStream(conversationId) : undefined}
									sending={sending}
									toolStatus={toolStatus}
									activeRunId={activeRunId}
									onMultiActionUpdate={handleMultiActionUpdate}
									onWriteProposalUpdate={handleWriteProposalUpdate}
									onWriteProposalGroupItemUpdate={handleWriteProposalGroupItemUpdate}
									onApprovalsGroupEmailItemUpdate={handleApprovalsGroupEmailItemUpdate}
									onEmailDraftUpdate={handleEmailDraftUpdate}
									onOfferDraftUpdate={handleOfferDraftUpdate}
									agents={mentionAgents}
									onAgentClick={onNavigateAgent}
									onTaskClick={onOpenTask}
									onRoutineClick={onOpenRoutine}
									onOpenDelegation={setDelegation}
									runsById={runsById}
									runsByMarker={runsByMarker}
									provenance={provenance}
									onSendFollowUp={async (text, runId, files) => {
										if (!conversationId) return;
										await streamMessage(conversationId, text, true, { runId, files });
									}}
									onRegenerate={async messageIndex => {
										if (!conversationId) return;
										const assistantMsg = messages[messageIndex];
										const runId =
											(assistantMsg?.metadata as { runId?: string } | undefined)?.runId ?? null;
										let userText: string | null = null;
										for (let j = messageIndex - 1; j >= 0; j--) {
											if (messages[j].role === 'user' && messages[j].content) {
												userText = messages[j].content;
												break;
											}
										}
										if (!userText && eventHookDetails) {
											userText = buildDryRunPrompt(eventHookDetails);
										}
										if (!userText && scheduleDetails?.prompt) {
											userText = scheduleDetails.prompt;
										}
										if (!userText) return;
										dispatchMessages({ type: 'truncateFrom', index: messageIndex });
										await streamMessage(conversationId, userText, false, {
											...(runId && { runId }),
											regenerate: true
										});
									}}
									onRetryLast={async () => {
										if (!conversationId) return;
										// Retry the most recent USER message — it may not be the
										// array tail if a failed/empty run left a trailing
										// contentless assistant message behind it.
										const lastUser = [...messages].reverse().find(m => m.role === 'user');
										if (!lastUser?.content) return;
										await streamMessage(conversationId, lastUser.content, false);
									}}
								/>
							</div>
						</div>

						{createdAgent && onNavigateAgent && (
							<div className="ws-agent-created-banner">
								<span>
									{createdAgent.updated ? T('assistant.agentUpdated') : T('assistant.agentCreated')}{' '}
									<strong>{createdAgent.name}</strong>
								</span>
								<button
									type="button"
									className="ws-btn ws-btn-primary ws-btn-sm"
									onClick={() => onNavigateAgent(createdAgent.agentId)}
								>
									<span>{T('assistant.configureAgent') || 'Configure'}</span>
									<ArrowRight style={{ width: 14, height: 14 }} />
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Routine-pinned conversations (schedule or event hook) hide the
			    global composer — each RunSection renders its own per-run
			    "Follow up on this run…" composer, so two composers would
			    duplicate the action. */}
			{!(!!scheduleDetails || !!eventHookDetails) && (
				<div className="ws-composer-wrap">
					{isEmptyChat && recentThread && (
						<div className="ws-chat-continue-bar">
							<button
								type="button"
								className="ws-cmd-continue-link"
								onClick={() => {
									if (onSelectConversation) {
										onSelectConversation(recentThread);
									} else {
										setConversationId(recentThread.id);
										setLastConversationId(recentThread.id);
									}
								}}
							>
								<span>{T('assistant.continueConversation')}</span>
								<ArrowRight style={{ width: 12, height: 12 }} />
							</button>
						</div>
					)}
					<ChatInput
						onSend={handleSend}
						disabled={userSending}
						streaming={sending}
						onStop={conversationId !== null ? () => stopStream(conversationId) : undefined}
						placeholder={`Ask ${assistantName || 'AI'} anything...`}
					/>
				</div>
			)}
			<DelegationDrawer
				delegation={delegation}
				onClose={() => setDelegation(null)}
				onOpenFull={cid => {
					setDelegation(null);
					setConversationId(cid);
					setLastConversationId(cid);
				}}
			>
				{delegation && (
					<WorkspaceChat
						assistantName={delegation.agentName || assistantName}
						avatarUrl={null}
						avatarColor={agentColor(delegation.agentId)}
						personalAgentId={personalAgentId}
						conversationId={delegation.conversationId}
						setConversationId={() => {}}
						selfId={selfId}
						contextType="agent"
						contextId={delegation.agentId}
						hideTopbar
						hideWelcome
						onNavigateAgent={onNavigateAgent}
						onOpenTask={onOpenTask}
						onOpenRoutine={onOpenRoutine}
					/>
				)}
			</DelegationDrawer>
		</>
	);
};

export default WorkspaceChat;
export { type Props as WorkspaceChatProps };
